import {
  canRestoreFromCache,
  canUseCachedData,
  createExportHistoryCache,
  type ExportHistoryCache,
  updateCache
} from '../services/exportHistoryCache';
import type {
  ExportFilter,
  ExportHistoryItem,
  HistoryApiResponse,
  HistoryFetchStatus
} from '../types/exportTypes';

interface UseExportCacheReturn {
  cacheRef: React.MutableRefObject<ExportHistoryCache>;
  canUseCache: (
    hasFilters: boolean,
    pageSize: number,
    currentPage: number,
    forceRefresh?: boolean
  ) => boolean;
  updateCacheData: (data: HistoryApiResponse, pageSize: number, currentPage: number) => void;
  restoreFromCache: (
    pageSize: number,
    setters: {
      setAvailableFilters: (filters: ExportFilter[]) => void;
      setTotalPages: (pages: number) => void;
      setCurrentPage: (page: number) => void;
      setExportHistory: (history: ExportHistoryItem[]) => void;
      setHistoryFetchStatus: (status: HistoryFetchStatus) => void;
    }
  ) => boolean;
}

export const useExportCache = (): UseExportCacheReturn => {
  const cacheRef = createExportHistoryCache();

  const canUseCache = (
    hasFilters: boolean,
    pageSize: number,
    currentPage: number,
    forceRefresh: boolean = false
  ) => {
    return canUseCachedData(cacheRef.current, hasFilters, pageSize, currentPage, forceRefresh);
  };

  const updateCacheData = (data: HistoryApiResponse, pageSize: number, currentPage: number) => {
    updateCache(cacheRef, data, pageSize, currentPage);
  };

  const restoreFromCache = (
    pageSize: number,
    setters: {
      setAvailableFilters: (filters: ExportFilter[]) => void;
      setTotalPages: (pages: number) => void;
      setCurrentPage: (page: number) => void;
      setExportHistory: (history: ExportHistoryItem[]) => void;
      setHistoryFetchStatus: (status: HistoryFetchStatus) => void;
    }
  ): boolean => {
    if (!canRestoreFromCache(cacheRef.current, pageSize)) {
      return false;
    }

    console.log('Restoring data from cache after filter reset');
    const cachedData = cacheRef.current.data!;

    // Update available filters if they're returned
    if (cachedData.filters) {
      setters.setAvailableFilters(cachedData.filters.map((f) => ({ ...f, values: [] })));
    }

    // Update pagination
    const totalRecords = cachedData.total_records;
    const recordsPerPage = cachedData.no_of_records_per_page;
    const calculatedTotalPages = Math.ceil(totalRecords / recordsPerPage);
    setters.setTotalPages(calculatedTotalPages);

    // Update export history
    setters.setExportHistory(cachedData.data);

    setters.setHistoryFetchStatus({
      isLoading: false,
      error: null,
      data: cachedData
    });

    return true;
  };

  return {
    cacheRef,
    canUseCache,
    updateCacheData,
    restoreFromCache
  };
};
